import { clog } from "@libs";
import { Typography } from "@mui/material";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import {
  ForwardRefRenderFunction,
  forwardRef,
  memo,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

export interface GenDatePickerProps extends Omit<
  DatePickerProps<Date>,
  "value"
> {
  value?: string | null;
  name?: string;
  error?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
}

const GenDatePicker: ForwardRefRenderFunction<
  DatePickerProps<Date>,
  GenDatePickerProps
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
> = (props, _ref) => {
  const { t } = useTranslation();
  const { label, required, value } = props;
  const [innerValue, setInnerValue] = useState<Date | null>(
    value?.length ? new Date(value) : null,
  );
  const [refresh, setRefresh] = useState<boolean>(false);

  useEffect(() => {
    // clog("GenDatePicker value changed:", value);
    if (value === null) setRefresh(true);
    setInnerValue(value !== null && value?.length ? new Date(value) : null);
  }, [value]);

  useEffect(() => {
    if (refresh) {
      setTimeout(() => {
        setRefresh(false);
      }, 0);
    }
  }, [refresh]);

  return !refresh ? (
    // <LocalizationProvider dateAdapter={AdapterDateFns}>
    <>
      <DatePicker
        {...props}
        value={innerValue}
        data-testid={`gen-date-picker-${props.name || "default"}`}
        label={
          <>
            {label}
            {required ? (
              <Typography component="span" color="error">
                *
              </Typography>
            ) : (
              ""
            )}
          </>
        }
        sx={{
          width: "100%",
          marginTop: 0,
          marginBottom: props.error?.length ? 0 : 1,
          marginY: 1,
          "& .MuiPickersInputBase-sectionsContainer": {
            padding: 1,
            paddingLeft: 0.25,
          },
          "& .MuiInputBase-input": {
            padding: 1.25,
            fontSize: "0.9rem",
          },
        }}
        format="dd/MM/yyyy"
        onChange={(value, context) => {
          // setInnerValue(value);
          if (value !== null && props.onChange) {
            props.onChange(value, context);
          } else if (value === null && props.onChange) {
            props.onChange(null, context);
          }
        }}
        onAccept={(value) => {
          // setInnerValue(value);
          if (value !== null && props.onAccept) {
            props.onAccept(value);
          } else if (value === null && props.onAccept) {
            props.onAccept(null);
          }
        }}
      />
      {props.placeholder?.length && (
        <Typography
          data-testid={`gen-date-picker-placeholder-${props.name || "default"}`}
          component={"div"}
          variant="caption"
          marginTop={0}
          marginLeft={1.5}
          padding={0}
          marginBottom={props.error?.length ? -1 : -1}
        >
          {t(props.placeholder)}
        </Typography>
      )}
      {props.error?.length && (
        <Typography
          data-testid={`gen-date-picker-error-${props.name || "default"}`}
          variant="caption"
          color="error"
          marginTop={-2}
          marginLeft={1.5}
        >
          {t(props.error)}
        </Typography>
      )}
    </>
  ) : null;
};

export default memo(forwardRef(GenDatePicker));
